/**
 * Interpreter Q&A corpus — deterministic generator (no AI credits).
 *
 * Every answer is self-contained, states no price, promises no outcome, and
 * never asserts an authority's internal practice as fixed: where practice
 * varies by office we say "ยืนยันกับหน่วยงานผู้รับ". Statutory references are
 * limited to the provisions cited in `./index.ts`.
 */
import {
  INTERPRETER_LANGUAGES,
  INTERPRETER_REVIEWED_TH,
  INTERPRETER_SCENARIOS,
  type InterpreterScenario,
} from "./index";
import { INTERPRETER_CITIES, INTERPRETER_INTENTS } from "./keywords";

import { expandWave } from "../_qa/wave-expander";
import { liftFlatToFloor } from "../_qa/topup-merge";

export type InterpreterQA = {
  slug: string;
  q: string;
  a: string;
  synonyms: string[];
  scenarioSlug?: string;
  languageSlug?: string;
};

const ASOF = `ข้อมูล ณ ${INTERPRETER_REVIEWED_TH} · ค่าบริการแจ้งเป็นรายเคส สอบถามทางโทรศัพท์ LINE หรืออีเมล`;

const CORE: InterpreterQA[] = [
  {
    slug: "interpreter-what-is",
    q: "บริการล่าม (Interpreter) ต่างจากงานแปลเอกสารอย่างไร",
    a: `ล่ามคือการแปลด้วยการพูดในเวลาจริง เช่น ในห้องพิจารณาคดี ที่ว่าการอำเภอ หรือห้องประชุม ส่วนงานแปลเอกสารเป็นการแปลเป็นลายลักษณ์อักษรที่ตรวจทานได้หลายรอบ งานราชการหลายอย่างต้องใช้ทั้งสองส่วนคู่กัน คือมีคำแปลเอกสารเป็นภาษาไทยและมีล่ามช่วยสื่อสารในวันนัด ${ASOF}`,
    synonyms: ["ล่ามกับนักแปลต่างกันอย่างไร", "interpreter vs translator", "ล่ามแปลสดคืออะไร"],
  },
  {
    slug: "interpreter-court-required",
    q: "คนต่างชาติที่ไม่เข้าใจภาษาไทยต้องมีล่ามในชั้นศาลหรือไม่",
    a: `ประมวลกฎหมายวิธีพิจารณาความอาญา มาตรา 13 กำหนดให้จัดหาล่ามเมื่อผู้ที่เกี่ยวข้องในคดีไม่สามารถพูดหรือเข้าใจภาษาไทยได้ ส่วนคดีแพ่ง ประมวลกฎหมายวิธีพิจารณาความแพ่ง มาตรา 46 กำหนดให้ศาลใช้ภาษาไทยและให้มีคำแปลเมื่อเอกสารเป็นภาษาต่างประเทศ การจะใช้ล่ามที่คู่ความจัดหาเองได้หรือไม่เป็นดุลพินิจของศาล จึงควรแจ้งศาลล่วงหน้าผ่านทนายความ ${ASOF}`,
    synonyms: ["กฎหมายบังคับให้มีล่ามในศาลไหม", "ล่ามศาลอาญา มาตรา 13", "court interpreter thailand law"],
    scenarioSlug: "court",
  },
  {
    slug: "interpreter-marriage-district",
    q: "จดทะเบียนสมรสกับชาวต่างชาติต้องมีล่ามไหม",
    a: `นายทะเบียนต้องมั่นใจว่าคู่สมรสเข้าใจข้อความในทะเบียนสมรสและยินยอมโดยสมัครใจ ในทางปฏิบัติสำนักทะเบียนหลายแห่งจึงขอให้มีล่ามเมื่อฝ่ายหนึ่งไม่เข้าใจภาษาไทย แนวปฏิบัติต่างกันเป็นรายสำนักทะเบียน จึงควรโทรยืนยันกับที่ว่าการอำเภอหรือสำนักงานเขตที่จะยื่นก่อนวันนัด พร้อมเตรียมหนังสือรับรองสถานภาพการสมรสที่แปลและผ่านนิติกรณ์กรมการกงสุลแล้ว ${ASOF}`,
    synonyms: ["ล่ามจดทะเบียนสมรสอำเภอ", "แต่งงานกับต่างชาติต้องมีล่าม", "marriage registration interpreter thailand"],
    scenarioSlug: "marriage",
  },
  {
    slug: "interpreter-relative-allowed",
    q: "ให้ญาติหรือเพื่อนเป็นล่ามได้ไหม",
    a: `หลายหน่วยงานไม่รับผู้ที่มีส่วนได้เสียในเรื่องนั้นเป็นล่าม เพราะกระทบความเป็นกลางของคำแปล โดยเฉพาะกระบวนการที่มีผลทางกฎหมาย เช่น การให้ปากคำ การลงนามในเอกสาร หรือการจดทะเบียนสถานะบุคคล แนวปฏิบัติต่างกันไปในแต่ละหน่วยงาน จึงควรยืนยันกับหน่วยงานผู้รับก่อน และควรใช้ล่ามภายนอกเมื่อเรื่องนั้นมีผลผูกพัน ${ASOF}`,
    synonyms: ["ญาติเป็นล่ามได้ไหม", "เพื่อนแปลให้ได้ไหมที่อำเภอ", "family member as interpreter thailand"],
  },
  {
    slug: "interpreter-remote-accepted",
    q: "หน่วยงานราชการรับล่ามทางไกล (VRI/OPI) หรือไม่",
    a: `ขึ้นอยู่กับหน่วยงานและลักษณะงาน งานเอกชนและการประชุมออนไลน์ใช้ล่ามทางไกลได้สะดวก แต่งานที่ต้องแสดงตัวต่อเจ้าพนักงาน เช่น การให้ปากคำ การจดทะเบียน หรือการลงนามต่อหน้าทนายความ ส่วนใหญ่ต้องใช้ล่ามในสถานที่ ควรสอบถามหน่วยงานเป็นลายลักษณ์อักษรก่อนนัดหมาย เพื่อไม่ให้ต้องเลื่อนนัด ${ASOF}`,
    synonyms: ["ล่ามออนไลน์ใช้กับราชการได้ไหม", "VRI OPI ล่าม", "remote interpreter accepted thailand"],
    scenarioSlug: "remote",
  },
  {
    slug: "interpreter-booking-lead-time",
    q: "ควรจองล่ามล่วงหน้ากี่วัน",
    a: `ภาษาที่ใช้บ่อย เช่น อังกฤษ จีน ญี่ปุ่น มักจัดคิวได้ในระยะเวลาสั้นกว่า ส่วนภาษาที่หาผู้ปฏิบัติงานได้น้อยควรจองล่วงหน้ามากกว่านั้น และงานที่ต้องเตรียมคำศัพท์เฉพาะควรส่งเอกสารให้ล่ามอย่างน้อย 1–2 วันทำการ เราไม่รับประกันคิวจนกว่าจะยืนยันวันเวลาและภาษาแล้ว จึงแนะนำให้ยืนยันคิวล่ามก่อนไปยืนยันวันนัดกับหน่วยงาน ${ASOF}`,
    synonyms: ["จองล่ามล่วงหน้านานไหม", "ล่ามด่วนวันเดียวได้ไหม", "interpreter booking lead time"],
  },
  {
    slug: "interpreter-confidentiality",
    q: "ล่ามรักษาความลับของข้อมูลอย่างไร",
    a: `ล่ามผูกพันตามหลักวิชาชีพในการรักษาความลับ ไม่นำข้อมูลของงานไปเปิดเผยหรือใช้ประโยชน์อื่น สำหรับงานที่มีข้อมูลอ่อนไหว เช่น คดีความ ข้อมูลสุขภาพ หรือข้อมูลทางการค้า สามารถจัดทำข้อตกลงรักษาความลับ (NDA) เป็นลายลักษณ์อักษรก่อนเริ่มงานได้ และควรส่งเอกสารผ่านช่องทางที่ควบคุมการเข้าถึงได้ ${ASOF}`,
    synonyms: ["ล่าม NDA", "ล่ามเก็บความลับไหม", "interpreter confidentiality nda"],
  },
  {
    slug: "interpreter-consecutive-vs-simultaneous",
    q: "ล่ามพูดตาม (consecutive) กับล่ามพูดพร้อม (simultaneous) เลือกแบบไหน",
    a: `ล่ามพูดตามเหมาะกับวงประชุมเล็ก การให้ปากคำ และงานราชการ เพราะแม่นยำและตรวจทานได้เป็นช่วง ส่วนล่ามพูดพร้อมเหมาะกับสัมมนาหรืองานที่มีผู้ฟังจำนวนมากและต้องการความต่อเนื่อง แต่ต้องมีอุปกรณ์รองรับและควรใช้ล่ามสองคนสลับกันเมื่อทำงานต่อเนื่องเกินประมาณหนึ่งชั่วโมง ${ASOF}`,
    synonyms: ["consecutive simultaneous ต่างกันอย่างไร", "ล่ามพูดพร้อมต้องใช้กี่คน", "simultaneous interpreter thailand"],
    scenarioSlug: "business",
  },
  {
    slug: "interpreter-notary-signing",
    q: "ลงนามหนังสือมอบอำนาจต่อหน้าทนายความ ต้องมีล่ามหรือไม่",
    a: `เมื่อผู้ลงนามไม่เข้าใจภาษาของเอกสาร การมีล่ามอ่านและแปลเอกสารทั้งฉบับให้ฟังต่อหน้าทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร ช่วยยืนยันว่าผู้ลงนามเข้าใจและยินยอม ลดข้อโต้แย้งเรื่องความยินยอมภายหลัง หน่วยงานปลายทางบางแห่งกำหนดให้มีถ้อยคำรับรองของล่ามในตัวเอกสาร จึงควรตรวจข้อกำหนดของผู้รับก่อนจัดทำ ${ASOF}`,
    synonyms: ["ล่ามเซ็นหนังสือมอบอำนาจ", "notary ต้องมีล่ามไหม", "interpreter for affidavit signing"],
    scenarioSlug: "notary",
  },
  {
    slug: "interpreter-scope-limits",
    q: "ขอบเขตหน้าที่ของล่ามคืออะไร ล่ามให้คำแนะนำได้ไหม",
    a: `ล่ามมีหน้าที่แปลสิ่งที่ผู้พูดสื่อสารให้ครบถ้วนและเป็นกลาง ไม่เพิ่ม ไม่ตัด และไม่ให้ความเห็นแทนคู่ความ ผู้ป่วย หรือคู่สัญญา คำแนะนำทางกฎหมายต้องมาจากทนายความ คำแนะนำทางการแพทย์ต้องมาจากแพทย์ การขอให้ล่ามย่อความหรือปรับคำตอบอาจทำให้บันทึกไม่ตรงกับข้อเท็จจริงและเกิดปัญหาในภายหลัง ${ASOF}`,
    synonyms: ["ล่ามให้คำปรึกษาได้ไหม", "หน้าที่ล่าม", "interpreter code of conduct"],
  },
];

function scenarioQA(s: InterpreterScenario): InterpreterQA[] {
  const out: InterpreterQA[] = [
    {
      slug: `${s.slug}-overview`,
      q: `${s.th} ทำงานอย่างไร`,
      a: `${s.summaryTh} สถานที่ปฏิบัติงานหลักคือ ${s.venueTh} ${ASOF}`,
      synonyms: [`${s.th} คืออะไร`, `${s.en.toLowerCase()} thailand`, `บริการ${s.th}`],
      scenarioSlug: s.slug,
    },
    {
      slug: `${s.slug}-documents`,
      q: `${s.th} ต้องเตรียมอะไรบ้าง`,
      a: `สิ่งที่ควรเตรียม: ${s.requirementsTh.join(" · ")} รายการอาจเปลี่ยนตามที่หน่วยงานผู้รับกำหนด จึงควรยืนยันก่อนวันนัด ${ASOF}`,
      synonyms: [`${s.th} เอกสารที่ต้องใช้`, `เตรียมตัว${s.th}`],
      scenarioSlug: s.slug,
    },
    {
      slug: `${s.slug}-steps`,
      q: `ขั้นตอนการใช้บริการ${s.th} มีอะไรบ้าง`,
      a: `${s.stepsTh.map((st, i) => `${i + 1}) ${st.title} — ${st.detail}`).join(" ")} ${ASOF}`,
      synonyms: [`ขั้นตอน${s.th}`, `${s.th} ทำอย่างไร`],
      scenarioSlug: s.slug,
    },
    {
      slug: `${s.slug}-warnings`,
      q: `${s.th} มีข้อควรระวังอะไร`,
      a: `ข้อควรระวังที่พบบ่อย: ${s.warningsTh.slice(0, 5).join(" · ")} ${s.citationTh} ${ASOF}`,
      synonyms: [`${s.th} ผิดพลาดบ่อย`, `${s.th} ถูกตีกลับเพราะอะไร`],
      scenarioSlug: s.slug,
    },
    {
      slug: `${s.slug}-tips`,
      q: `มีคำแนะนำอะไรก่อนใช้บริการ${s.th}`,
      a: `คำแนะนำก่อนวันนัด: ${s.tipsTh.slice(0, 5).join(" · ")} ${ASOF}`,
      synonyms: [`เตรียมตัวก่อน${s.th}`, `${s.th} ทำอย่างไรให้ราบรื่น`],
      scenarioSlug: s.slug,
    },
  ];

  for (const l of INTERPRETER_LANGUAGES) {
    out.push({
      slug: `${s.slug}-lang-${l.slug}`,
      q: `${s.th} ภาษา${l.th} จัดให้ได้หรือไม่`,
      a: `เรารับจัดล่ามภาษา${l.th} (${l.en}) สำหรับงาน${s.th} ณ ${s.venueTh} โดยยืนยันคิวตามวันเวลาที่ว่างจริง ก่อนนัดควรตรวจกับหน่วยงานผู้รับว่ารับล่ามที่ผู้ใช้บริการจัดหาเองหรือไม่ และต้องแสดงเอกสารประจำตัวของล่ามหรือไม่ ${ASOF}`,
      synonyms: [
        `ล่ามภาษา${l.th} ${s.th}`,
        `${l.en.toLowerCase()} interpreter ${s.en.toLowerCase()}`,
      ],
      scenarioSlug: s.slug,
      languageSlug: l.slug,
    });
  }

  for (const c of INTERPRETER_CITIES) {
    out.push({
      slug: `${s.slug}-city-${c.slug}`,
      q: `${s.th} ที่${c.th} ให้บริการไหม`,
      a: `ให้บริการงาน${s.th} ในพื้นที่${c.th} โดยนัดหมายล่วงหน้าเพื่อจัดคิวล่ามและเวลาเดินทาง กรณีนอกเขตกรุงเทพและปริมณฑลควรแจ้งล่วงหน้ามากขึ้น เพราะภาษาที่หาผู้ปฏิบัติงานได้น้อยอาจต้องเดินทางจากพื้นที่อื่น ${ASOF}`,
      synonyms: [`ล่าม${c.th}`, `${s.th} ${c.th}`],
      scenarioSlug: s.slug,
    });
  }

  return out;
}

function intentQA(): InterpreterQA[] {
  return INTERPRETER_INTENTS.map((i) => ({
    slug: `intent-${i.slug}`,
    q: `ต้องใช้ล่ามหรือไม่เมื่อจะ${i.th}`,
    a: `สำหรับการ${i.th} หากฝ่ายใดฝ่ายหนึ่งไม่เข้าใจภาษาที่ใช้ในกระบวนการนั้น การมีล่ามช่วยให้คำถาม-คำตอบและข้อความในเอกสารถูกเข้าใจตรงกัน และลดความเสี่ยงที่เอกสารจะถูกตีกลับหรือถูกโต้แย้งภายหลัง ข้อกำหนดว่าต้องมีล่ามหรือไม่เป็นไปตามที่หน่วยงานผู้รับกำหนด จึงควรสอบถามยืนยันก่อนวันนัด ${ASOF}`,
    synonyms: [`ล่าม${i.th}`, `${i.th} ต้องมีล่ามไหม`],
  }));
}

const merged: InterpreterQA[] = [];
const seen = new Set<string>();
const WAVE = expandWave({ prefix: "interp", asOf: ASOF, authorityTh: "หน่วยงานหรือศาลผู้รับเรื่อง", clusterTh: "งานล่ามวิชาชีพ", primary: [...INTERPRETER_SCENARIOS, ...INTERPRETER_LANGUAGES], secondary: INTERPRETER_CITIES, intents: INTERPRETER_INTENTS });

for (const qa of [...WAVE, ...CORE, ...INTERPRETER_SCENARIOS.flatMap(scenarioQA), ...intentQA()]) {
  if (seen.has(qa.slug)) continue;
  seen.add(qa.slug);
  merged.push(qa);
}

liftFlatToFloor(merged, {
  prefix: "interp-t7",
  asOf: ASOF,
  authorityTh: "หน่วยงานหรือศาลผู้รับเรื่อง",
  clusterTh: "งานล่ามวิชาชีพ",
  primary: [...INTERPRETER_SCENARIOS, ...INTERPRETER_LANGUAGES].map((x: any) => ({
    slug: x.slug,
    th: x.th ?? x.nameTh,
    ...(x.en ?? x.nameEn ? { en: x.en ?? x.nameEn } : {}),
  })),
});

export const INTERPRETER_QA_ALL: InterpreterQA[] = merged;

export const INTERPRETER_QA_BY_SLUG: Record<string, InterpreterQA> = Object.fromEntries(
  INTERPRETER_QA_ALL.map((q) => [q.slug, q]),
);

export const INTERPRETER_QA_PHRASING_COUNT = INTERPRETER_QA_ALL.reduce(
  (n, qa) => n + 1 + qa.synonyms.length,
  0,
);
